import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import type { Me } from '../lib/api';
import WaveformLane from '../components/Monitor/WaveformLane';
import type { Rhythm } from '../lib/waveforms';

const PREVIEW_RHYTHMS: Array<{ rhythm: Rhythm; hr: number; label: string }> = [
  { rhythm: 'sinus',    hr: 75,  label: 'Sinus 75' },
  { rhythm: 'vf',       hr: 0,   label: 'VF — SHOCKABLE' },
  { rhythm: 'aflutter', hr: 150, label: 'AFlutter 2:1 → 150' },
  { rhythm: 'torsades', hr: 0,   label: 'Torsades — Mg' },
  { rhythm: 'chb',      hr: 32,  label: 'CHB → pacing' },
];

export default function Landing({ me }: { me: Me | null }) {
  // Live preview ticker — cycles through five clinically-important rhythms on a 5s loop
  const [idx, setIdx] = useState(0);
  const [tStart] = useState(Date.now());
  useEffect(() => { const id = setInterval(() => setIdx((i) => (i + 1) % PREVIEW_RHYTHMS.length), 5000); return () => clearInterval(id); }, []);
  // Animate the strip
  const [, setTick] = useState(0);
  useEffect(() => { const id = setInterval(() => setTick((t) => t + 1), 80); return () => clearInterval(id); }, []);
  const preview = PREVIEW_RHYTHMS[idx];
  const sim_t_ms = Date.now() - tStart;
  return (
    <div className="max-w-7xl mx-auto px-4 py-16">
      <div className="grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <h1 className="text-4xl lg:text-5xl font-semibold tracking-tight">
            Practise the next 8 minutes <span className="text-brand-600">before they happen.</span>
          </h1>
          <p className="mt-6 text-mono-white/70 text-lg leading-relaxed">
            Browser-based Advanced Life Support training. UK Resuscitation Council aligned.
            Physiologically accurate waveforms, real-time interactive scenarios, and
            instructor-grade analytics.
          </p>
          <div className="mt-8 flex gap-4">
            {me ? (
              <Link to="/dashboard" className="btn-primary">Go to dashboard</Link>
            ) : (
              <>
                <Link to="/try" className="btn-primary">Try a 5-minute taster · no signup</Link>
                <Link to="/login" className="btn-ghost">Sign in</Link>
              </>
            )}
          </div>
          <p className="mt-6 text-xs text-mono-white/40">
            Practice tool. Not for clinical decision-making. Scenarios authored by Claude Code sessions with human review.
          </p>
        </div>
        <div className="panel p-4 bg-ink-950">
          <div className="flex justify-between items-center mb-2">
            <div className="text-xs text-mono-white/60 uppercase tracking-widest">Live monitor preview</div>
            <div className="text-xs text-mono-green font-mono tabular-nums">{preview.label}</div>
          </div>
          <WaveformLane
            lane="ecg"
            vitals={{ rhythm: preview.rhythm, hr: preview.hr, spo2: 98, etco2_mmhg: 38, rr: 14 }}
            sim_t_ms={sim_t_ms}
            cpr_active={false}
            cpr_quality_pct={0}
            cpr_rate_cpm={0}
            intubated={false}
            height={120}
          />
          <div className="flex justify-between mt-2 text-[10px] text-mono-white/30 font-mono tabular-nums">
            <span>25 mm/s · 10 mV/mV</span>
            <span>cycling through 5 rhythms</span>
          </div>
        </div>
      </div>

      <section id="how" className="mt-24 grid md:grid-cols-3 gap-6">
        <Feature title="Real protocols" body="Every scenario carries RCUK / AHA / ERC citations — no LLM-hallucinated doses." />
        <Feature title="Realtime physiology" body="Deterministic FSM at 10Hz. Every shock, drug, compression has the right effect, every time." />
        <Feature title="Cohort analytics" body="Instructor dashboard with run history, weak-area chips, CSV export. Built for ALS faculty." />
      </section>
    </div>
  );
}

function Feature({ title, body }: { title: string; body: string }) {
  return (
    <div className="panel p-5">
      <h3 className="font-semibold text-mono-white">{title}</h3>
      <p className="text-sm text-mono-white/60 mt-2 leading-relaxed">{body}</p>
    </div>
  );
}
